// File: components/renderer/player/drawRumi.ts
import { Player } from "../../types/entities";
import { COLORS } from "../../config/colors";
import { drawBraid, drawFrontHair, drawHead, drawJacket, drawLegs, drawShorts, drawTorso, drawNeck } from "./parts";
import { CostumeId } from "../../../types";

// --- WEAPON RENDERER ---
const drawSaingeom = (ctx: CanvasRenderingContext2D, isPhaseTwo: boolean) => {
    ctx.save();

    // Common Attributes: Gold Hilt
    const goldGrad = ctx.createLinearGradient(0, 0, 10, 0);
    goldGrad.addColorStop(0, "#ffd700"); goldGrad.addColorStop(0.5, "#fff8dc"); goldGrad.addColorStop(1, "#b8860b");

    if (!isPhaseTwo) {
        // --- PHASE ONE: SAINGEOM (Straight, Constellations) ---
        ctx.fillStyle = "#8e44ad";
        ctx.beginPath(); ctx.arc(0, 0, 6, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = goldGrad;
        ctx.fillRect(-3, -25, 6, 25);
        ctx.strokeStyle = "#b8860b"; ctx.lineWidth = 1;
        ctx.beginPath(); ctx.moveTo(-3, -20); ctx.lineTo(3, -22); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(-3, -10); ctx.lineTo(3, -12); ctx.stroke();
        ctx.fillStyle = goldGrad;
        ctx.fillRect(-12, -28, 24, 6);
        ctx.translate(0, -28);
        const bladeGrad = ctx.createLinearGradient(0, 0, 0, -100);
        bladeGrad.addColorStop(0, "rgba(76, 201, 240, 0.8)");
        bladeGrad.addColorStop(0.5, "rgba(67, 97, 238, 0.4)");
        bladeGrad.addColorStop(1, "rgba(255, 255, 255, 0.9)");
        ctx.fillStyle = bladeGrad;
        ctx.beginPath();
        ctx.moveTo(-4, 0); ctx.lineTo(4, 0);
        ctx.lineTo(3, -90);
        ctx.lineTo(0, -100);
        ctx.lineTo(-3, -90);
        ctx.closePath();
        ctx.fill();
        ctx.fillStyle = "#fff";
        ctx.shadowColor = "#fff"; ctx.shadowBlur = 5;
        ctx.beginPath(); ctx.arc(0, -20, 1.5, 0, Math.PI * 2); ctx.fill();
        ctx.beginPath(); ctx.arc(0, -50, 1.5, 0, Math.PI * 2); ctx.fill();
        ctx.beginPath(); ctx.arc(0, -80, 2, 0, Math.PI * 2); ctx.fill();
        ctx.shadowBlur = 0;
    } else {
        // --- PHASE TWO: AWAKENED ---
        ctx.fillStyle = goldGrad;
        ctx.beginPath(); ctx.arc(0, 0, 8, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = "#a855f7";
        ctx.beginPath(); ctx.arc(0, 0, 3, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = "#333";
        ctx.fillRect(-4, -30, 8, 30);
        ctx.strokeStyle = goldGrad; ctx.lineWidth = 2;
        ctx.beginPath(); ctx.moveTo(-4, -10); ctx.lineTo(4, -10); ctx.stroke();
        ctx.fillStyle = goldGrad;
        ctx.beginPath();
        ctx.moveTo(0, -30);
        ctx.quadraticCurveTo(20, -20, 25, -45);
        ctx.lineTo(5, -35);
        ctx.lineTo(-5, -35);
        ctx.lineTo(-25, -45);
        ctx.quadraticCurveTo(-20, -20, 0, -30);
        ctx.fill();
        ctx.translate(0, -35);
        ctx.fillStyle = "#e0e0e0";
        ctx.beginPath(); ctx.arc(0, -10, 10, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = "#ff0000";
        ctx.beginPath(); ctx.arc(-3, -12, 1.5, 0, Math.PI * 2); ctx.fill();
        ctx.beginPath(); ctx.arc(3, -12, 1.5, 0, Math.PI * 2); ctx.fill();
        const bladeGrad = ctx.createLinearGradient(0, -20, 0, -120);
        bladeGrad.addColorStop(0, "rgba(168, 85, 247, 0.6)");
        bladeGrad.addColorStop(0.5, "rgba(76, 201, 240, 0.6)");
        bladeGrad.addColorStop(1, "rgba(255, 255, 255, 0.9)");
        ctx.fillStyle = bladeGrad;
        ctx.beginPath();
        ctx.moveTo(-8, -15);
        ctx.quadraticCurveTo(15, -60, 0, -130);
        ctx.quadraticCurveTo(-5, -60, -8, -15);
        ctx.fill();
        ctx.strokeStyle = "rgba(255,255,255,0.8)"; ctx.lineWidth = 1;
        ctx.beginPath(); ctx.moveTo(0, -30); ctx.lineTo(0, -110); ctx.stroke();
    }
    ctx.restore();
};

export const drawRumi = (ctx: CanvasRenderingContext2D, p: Player, isPhaseTwo: boolean = false, costume: CostumeId = 'DEFAULT', hideWeapon: boolean = false) => {
    ctx.save();
    const cx = p.x + p.width / 2;
    const cy = p.y + p.height;
    ctx.translate(cx, cy);
    ctx.scale(p.facing, 1);

    const scale = p.height / 180;
    ctx.scale(scale, scale);

    const breath = Math.sin(Date.now() / 300) * 1.5;
    const walkSway = Math.sin(Date.now() / 150) * (Math.abs(p.vx) > 0.1 ? 5 : 0);
    const braidSway = Math.sin(Date.now() / 150 + 1) * (Math.abs(p.vx) > 0.1 ? 15 : 4);

    const skinGrad = ctx.createLinearGradient(-15, -90, 15, -90);
    skinGrad.addColorStop(0, "#e8c39e"); skinGrad.addColorStop(0.5, "#ffdbac"); skinGrad.addColorStop(1, "#e8c39e");

    const headY = -148 + breath; // Lowered from -152

    if (costume === 'DEFAULT') {
        ctx.save();
        ctx.translate(0, headY);
        drawBraid(ctx, breath, braidSway);
        ctx.restore();

        drawLegs(ctx, skinGrad, walkSway);
        drawShorts(ctx, breath);

        // Neck before torso/jacket layers
        ctx.save();
        ctx.translate(0, headY);
        drawNeck(ctx, skinGrad);
        ctx.restore();

        drawTorso(ctx, skinGrad);
        if (!p.isSwinging && !hideWeapon) drawSwordOnBack(ctx, breath, p.facing, isPhaseTwo);
        drawJacket(ctx, skinGrad, p.isSwinging);

        ctx.save();
        ctx.translate(0, headY);
        drawHead(ctx, skinGrad, isPhaseTwo);
        drawFrontHair(ctx);
        ctx.restore();
    }
    else if (costume === 'PAJAMAS') {
        const NAVY = "#1a1b3a";
        ctx.fillStyle = NAVY;
        ctx.beginPath();
        ctx.moveTo(-15, -85 + breath); ctx.lineTo(15, -85 + breath);
        ctx.lineTo(18, 0); ctx.lineTo(-18, 0);
        ctx.fill();

        ctx.fillStyle = "rgba(255,255,255,0.4)";
        for (let i = 0; i < 10; i++) {
            for (let j = 0; j < 4; j++) {
                ctx.beginPath(); ctx.arc(-12 + j * 8, -80 + i * 10 + breath, 1, 0, Math.PI * 2); ctx.fill();
            }
        }

        const drawTeddy = (tx: number, ty: number) => {
            ctx.save(); ctx.translate(tx, ty);
            ctx.fillStyle = "#8d6e63";
            ctx.beginPath(); ctx.arc(0, 0, 3.5, 0, Math.PI * 2); ctx.fill();
            ctx.beginPath(); ctx.arc(-2.5, -3, 1.5, 0, Math.PI * 2); ctx.fill();
            ctx.beginPath(); ctx.arc(2.5, -3, 1.5, 0, Math.PI * 2); ctx.fill();
            ctx.fillStyle = "#4caf50";
            ctx.fillRect(-3, 2, 6, 2);
            ctx.restore();
        };
        drawTeddy(-8, -60 + breath);
        drawTeddy(6, -20 + breath);

        const HOODIE_GREY = "#bdc3c7";
        ctx.fillStyle = HOODIE_GREY;
        ctx.beginPath(); ctx.roundRect(-18, -145 + breath, 36, 65, 4); ctx.fill();

        const drawSleeve = (x: number, rot: number) => {
            ctx.save();
            ctx.translate(x, -140 + breath);
            ctx.rotate(rot);
            ctx.fillStyle = HOODIE_GREY;
            ctx.beginPath(); ctx.roundRect(-6, 0, 12, 50, 4); ctx.fill();
            ctx.restore();
        };
        drawSleeve(-18, 0.2);
        drawSleeve(18, -0.2);

        ctx.save();
        ctx.translate(0, headY);
        ctx.fillStyle = HOODIE_GREY;
        ctx.beginPath(); ctx.arc(0, -15, 25, 0, Math.PI * 2); ctx.fill();
        drawNeck(ctx, skinGrad);
        drawHead(ctx, skinGrad, isPhaseTwo);
        drawFrontHair(ctx);
        ctx.restore();

        if (!p.isSwinging && !hideWeapon) drawSwordOnBack(ctx, breath, p.facing, isPhaseTwo);
    }
    else if (costume === 'LEATHER') {
        ctx.save();
        ctx.translate(0, headY);
        drawBraid(ctx, breath, braidSway);
        ctx.restore();
        ctx.fillStyle = "#111";
        ctx.beginPath(); ctx.moveTo(-10, -90); ctx.lineTo(-15, 0); ctx.lineTo(15, 0); ctx.lineTo(10, -90); ctx.fill();

        ctx.save();
        ctx.translate(0, headY);
        drawNeck(ctx, skinGrad);
        ctx.restore();

        ctx.fillStyle = "#000";
        ctx.fillRect(-15, -150 + breath, 30, 60);
        ctx.fillStyle = "rgba(255,255,255,0.2)";
        ctx.beginPath(); ctx.moveTo(-15, -150 + breath); ctx.lineTo(-5, -150 + breath); ctx.lineTo(-10, -90 + breath); ctx.fill();
        if (!p.isSwinging && !hideWeapon) drawSwordOnBack(ctx, breath, p.facing, isPhaseTwo);

        ctx.save();
        ctx.translate(0, headY);
        drawHead(ctx, skinGrad, isPhaseTwo);
        drawFrontHair(ctx);
        ctx.restore();
    }
    else if (costume === 'HANBOK') {
        ctx.save();
        ctx.translate(0, headY);
        drawBraid(ctx, breath, braidSway);
        ctx.restore();
        ctx.fillStyle = "#b91c1c";
        ctx.beginPath(); ctx.moveTo(0, -120 + breath); ctx.lineTo(-40, 0); ctx.lineTo(40, 0); ctx.fill();

        ctx.save();
        ctx.translate(0, headY);
        drawNeck(ctx, skinGrad);
        ctx.restore();

        ctx.fillStyle = "#fff";
        ctx.fillRect(-15, -150 + breath, 30, 30);
        ctx.fillStyle = "#111";
        ctx.beginPath(); ctx.moveTo(-15, -150 + breath); ctx.lineTo(0, -120 + breath); ctx.lineTo(15, -150 + breath); ctx.stroke();
        if (!p.isSwinging && !hideWeapon) drawSwordOnBack(ctx, breath, p.facing, isPhaseTwo);

        ctx.save();
        ctx.translate(0, headY);
        drawHead(ctx, skinGrad, isPhaseTwo);
        drawFrontHair(ctx);
        ctx.restore();
    }

    ctx.restore();
};

const drawSwordOnBack = (ctx: CanvasRenderingContext2D, breath: number, facing: number, isPhaseTwo: boolean) => {
    ctx.save();
    ctx.translate(0, -135 + breath);
    ctx.rotate(Math.PI / 4 * -facing);
    ctx.translate(15, 10);
    ctx.scale(0.7, 0.7);
    ctx.rotate(Math.PI);
    drawSaingeom(ctx, isPhaseTwo);
    ctx.restore();
};

export const drawSwing = (ctx: CanvasRenderingContext2D, p: Player, isPhaseTwo: boolean) => {
    ctx.save();
    const cy = p.y + p.height * 0.45;
    const cx = p.x + p.width / 2;
    ctx.translate(cx, cy);
    ctx.scale(p.facing, 1);
    ctx.save();
    const swingProgress = p.swingTimer / 10;
    const angle = -Math.PI / 2 + (Math.PI * (1 - swingProgress));
    ctx.rotate(angle);
    ctx.translate(20, 0);
    ctx.rotate(Math.PI / 2);
    drawSaingeom(ctx, isPhaseTwo);
    ctx.restore();
    ctx.globalCompositeOperation = "screen";
    ctx.shadowBlur = 20;
    ctx.shadowColor = isPhaseTwo ? "#d946ef" : "#00ffff";
    const trailGrad = ctx.createConicGradient(0, 0, 0);
    if (isPhaseTwo) {
        trailGrad.addColorStop(0, "rgba(168, 85, 247, 0)");
        trailGrad.addColorStop(0.5, "rgba(168, 85, 247, 0.8)");
        trailGrad.addColorStop(1, "rgba(255, 255, 255, 1)");
    } else {
        trailGrad.addColorStop(0, "rgba(0, 255, 255, 0)");
        trailGrad.addColorStop(0.5, "rgba(0, 255, 255, 0.8)");
        trailGrad.addColorStop(1, "rgba(255, 255, 255, 1)");
    }
    ctx.fillStyle = trailGrad;
    ctx.beginPath();
    ctx.arc(0, 0, 150, -Math.PI / 2, angle);
    ctx.lineTo(0, 0);
    ctx.fill();
    ctx.strokeStyle = "rgba(255, 255, 255, 0.5)";
    ctx.lineWidth = 2;
    ctx.beginPath();
    for (let i = 0; i < 3; i++) {
        const r = 80 + i * 30;
        ctx.arc(0, 0, r, -Math.PI / 2, angle);
    }
    ctx.stroke();
    ctx.restore();
};